import React from "react";
import {
    Package,
    ShoppingCart,
    Users,
    TrendingUp,
    Wallet,
    Receipt,
    FileText,
    TrendingDown,
    Minus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const iconMap = {
    "Total Products": Package,
    "Total Orders": ShoppingCart,
    "Total Customers": Users,
    "Total Sales": TrendingUp,
    "Total Expenses": Wallet,
    "Wallet Balance": Wallet,
    "Total Receipts": Receipt,
    "Total Invoice": FileText,
};

const gradientThemes = [
    { blur: "from-blue-500/40 to-purple-500/40", iconBg: "bg-indigo-100 text-indigo-600" },
    { blur: "from-emerald-500/40 to-teal-500/40", iconBg: "bg-green-100 text-green-600" },
    { blur: "from-pink-500/40 to-rose-500/40", iconBg: "bg-pink-100 text-pink-600" },
    { blur: "from-amber-500/40 to-orange-500/40", iconBg: "bg-yellow-100 text-yellow-600" },
    { blur: "from-red-500/40 to-rose-500/40", iconBg: "bg-red-100 text-red-600" },
    { blur: "from-indigo-500/40 to-blue-500/40", iconBg: "bg-blue-100 text-blue-600" },
    { blur: "from-purple-500/40 to-fuchsia-500/40", iconBg: "bg-purple-100 text-purple-600" },
    { blur: "from-sky-500/40 to-cyan-500/40", iconBg: "bg-sky-100 text-sky-600" },
];

const cardsConfig = [
    { title: "Total Products", key: "products", path: "/products" },
    { title: "Total Orders", key: "orders", path: "/orders" },
    { title: "Total Customers", key: "customers", path: "/customer" },
    { title: "Total Sales", key: "sales", path: "/orders" },
    { title: "Total Expenses", key: "expenses", path: "/expense" },
    { title: "Wallet Balance", key: "sales", path: "/wallet-target" },
    { title: "Total Receipts", key: "receipts", path: "/receipt" },
    { title: "Total Invoice", key: "invoices", path: "/invoice" },
];

const DashboardCards = ({ current, previous }) => {
    return (
        <div className="flex gap-4 overflow-x-auto py-3 no-scrollbar snap-x snap-mandatory mt-3">
            {cardsConfig.map((cfg, idx) => (
                <DashboardCard
                    key={cfg.title}
                    title={cfg.title}
                    current={current[cfg.key]}
                    last={previous[cfg.key]}
                    index={idx}
                    path={cfg.path}
                />
            ))}
        </div>
    );
};

export default DashboardCards;

const DashboardCard = ({ title, current, last, index, path }) => {
    const navigate = useNavigate();
    const handleClick = () => navigate(path);
    const Icon = iconMap[title];
    const theme = gradientThemes[index % gradientThemes.length];
    const diff = current - last;
    const percentage = last === 0 ? "—%" : ((diff / last) * 100).toFixed(1);
    const percentColor = diff > 0 ? "text-green-500" : diff < 0 ? "text-red-500" : "text-yellow-500";
    const percentIcon =
        diff > 0 ? <TrendingUp size={14} /> :
            diff < 0 ? <TrendingDown size={14} /> :
                <Minus size={14} />; // Neutral


    const isMoney = ["Sales", "Expenses", "Receipts", "Wallet"].some((w) => title.includes(w));
    const formatValue = (v) => (isMoney ? `₹${v.toLocaleString("en-IN")}` : v.toLocaleString("en-IN"));

    return (
        <div 
         onClick={handleClick}
        className="relative snap-center bg-white shadow-md border border-gray-200 rounded-xl px-3 py-2 min-w-60 shrink-0 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden">
            <div className={`absolute top-0 left-0 w-32 h-32 bg-gradient-to-br ${theme.blur} blur-2xl opacity-60`} />
            <div className="relative z-10">
                <div className="flex justify-between items-center mb-2">
                    <h2 className="text-lg font-semibold text-gray-700">{title}</h2>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm ${theme.iconBg}`}>
                        <Icon size={20} strokeWidth={2.5} />
                    </div>
                </div>

                <p className="text-3xl font-extrabold text-gray-900">{formatValue(current)}</p>

                <div className="flex justify-between items-center text-sm text-gray-600">
                    <span>Last Period</span>
                    <span className="font-semibold text-gray-900">{formatValue(last)}</span>
                </div>

                <div className={`mt-1 flex items-center gap-1 font-semibold text-xs ${percentColor}`}>
                    {percentIcon} {percentage}%
                </div>
            </div>
        </div>
    );
};